import {Close} from '@mui/icons-material';
import {UserFilled, Document, Video} from '@carbon/icons-react';
import {Box, Grid, IconButton, Link, Stack, Typography, useTheme} from '@mui/material';
import {OnboardingActions, selectOnboarding, setOnboarding} from 'app/store/appState/appStateSlice';
import {selectAllArtifacts} from 'app/store/artifacts/artifactSlice';
import {selectUserType} from 'app/store/users/userSlice';
import {CardType, LogInType} from 'app/types/Shared';
import {ArtifactCards} from 'components/ArtifactCards';
import {useSelector, useDispatch} from 'react-redux';

type IconProps = {
  color: string;
};

enum OnboardingType {
  Video = 'Onboarding Video',
  Guide = 'Onboarding Guide',
}

const Cursor_2Icon = ({color}: IconProps) => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M20.5045 9.0445L4.0045 3.0445C3.87055 2.99618 3.72561 2.98699 3.58662 3.01801C3.44764 3.04903 3.32036 3.11898 3.21967 3.21967C3.11898 3.32036 3.04903 3.44764 3.01801 3.58662C2.98699 3.72561 2.99618 3.87055 3.0445 4.0045L9.0445 20.5045C9.09691 20.6495 9.1927 20.7747 9.31885 20.8633C9.44499 20.9519 9.59537 20.9994 9.7495 20.9995C9.89971 20.9996 10.0465 20.9547 10.1709 20.8704C10.2952 20.7862 10.3914 20.6665 10.447 20.527L13.327 13.327L20.527 10.447C20.6693 10.393 20.7918 10.297 20.8783 10.1718C20.9647 10.0465 21.011 9.89794 21.011 9.74575C21.011 9.59356 20.9647 9.44497 20.8783 9.31972C20.7918 9.19446 20.6693 9.09848 20.527 9.0445H20.5045ZM12.472 12.0445L12.172 12.1645L12.052 12.4645L9.7495 18.1495L5.002 5.002L18.1495 9.7495L12.472 12.0445Z'
      fill={color}
    />
    <path
      d='M7.875 5.25L4.5 4.5H4.125L7.875 15.75L9.75 19.5L12 14.625L13.125 12.375L17.625 10.5L19.125 9.75L7.875 5.25Z'
      fill={color}
    />
  </svg>
);

const CheckmarkIcon = ({color}: IconProps) => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M6 11.1726L9.97241 15.6416L18.4138 7.2002'
      stroke={color}
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
type HelpPageProps = {
  onHome?: () => Promise<void>;
};
const HelpPage = ({onHome}: HelpPageProps) => {
  const theme = useTheme();
  const userType = useSelector(selectUserType);
  const allArtifacts = useSelector(selectAllArtifacts);
  const oboarding = useSelector(selectOnboarding);
  const dispatch = useDispatch();

  const onboardingVideos = allArtifacts?.filter((artifact) => artifact.type === OnboardingType.Video);
  const onboardingGuides = allArtifacts?.filter((artifact) => artifact.type === OnboardingType.Guide);

  const clickGuideMe = () => {
    const guideMe = document.getElementById('Guide_Me_Role_Component');
    guideMe?.click();
  };

  const helpGuide: Array<{icon: JSX.Element; title: string; instructions: JSX.Element}> = [
    {
      icon: <CheckmarkIcon color={theme.palette.grey[800]} />,
      title: 'Select a Function',
      instructions: (
        <Typography variant='h6' fontSize='14px'>
          Click{' '}
          <Link
            fontWeight={600}
            sx={{cursor: 'pointer', color: theme.palette.primary.main}}
            underline='hover'
            onClick={() => clickGuideMe()}
          >
            Guide Me
          </Link>{' '}
          in the upper right and locate your functional area
        </Typography>
      ),
    },
    {
      icon: <UserFilled color='grey.800' width='24px' height='24px' />,
      title: 'Follow the Icon',
      instructions: (
        <Typography variant='h6' fontSize='14px'>
          A profile icon will display next to content relevant to your selection
        </Typography>
      ),
    },
    {
      icon: <Cursor_2Icon color={theme.palette.grey[800]} />,
      title: 'Explore Processes',
      instructions: (
        <Typography variant='h6' fontSize='14px'>
          Select a{' '}
          <Link
            fontWeight={600}
            sx={{cursor: 'pointer', color: theme.palette.primary.main}}
            underline='hover'
            onClick={() => onHome && onHome()}
          >
            Trial Delivery Focus Area
          </Link>{' '}
          to view specific processes
        </Typography>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '79vh',
        px: 6,
        pt: 4,
        pb: 6,
        background: theme.palette.grey[100],
        borderTop: `1px solid ${theme.palette.grey[500]}`,
      }}
    >
      <Grid container spacing='20px' direction='column'>
        {oboarding?.helpGuide && userType === LogInType.Internal && (
          <Grid container item direction='row'>
            <Grid
              item
              container
              xs={12}
              lg={8}
              xl={7}
              rowGap='10px'
              sx={{
                width: '100%',
                minHeight: 225,
                bgcolor: theme.palette.grey[300],
                border: `1px solid ${theme.palette.grey[500]}`,
                boxShadow: '0px 0px 10px 0px #00000040',
                p: 1,
              }}
            >
              <Grid item xs={11} display='flex' p={1} height={20}>
                <Typography variant='h5'>How to Locate a Process</Typography>
              </Grid>
              <Grid item xs={1} display='flex' justifyContent='flex-end' alignItems='flex-start' height={20}>
                <IconButton sx={{p: 0}} onClick={() => dispatch(setOnboarding(OnboardingActions.DisableHelpGuide))}>
                  <Close fontSize='small' sx={{color: theme.palette.grey[700]}} />
                </IconButton>
              </Grid>
              {helpGuide.map((item, index) => (
                <Grid
                  item
                  xs={3.6}
                  key={`Help_Guide--${index}`}
                  border={`1px solid ${theme.palette.grey[500]}`}
                  mx={1}
                  display='flex'
                  justifyContent='center'
                >
                  <Stack maxWidth={300} alignItems='center' direction='column' spacing={1} textAlign='center' p={2}>
                    {item.icon}
                    <Typography variant='h1' fontSize='18px'>
                      {item.title}
                    </Typography>
                    {item.instructions}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          {!!onboardingVideos?.length && (
            <Stack spacing={1} justifyContent='flex-start'>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-start' pl={1}>
                <Video height='22px' width='22px' />
                <Typography variant='h5'> Videos</Typography>
              </Stack>

              <Grid container spacing={1}>
                {onboardingVideos?.map((item) => (
                  <Grid item xs={12} md={6} lg={4} xl={3} key={item.key}>
                    <ArtifactCards
                      data={{
                        ...item,
                        card: CardType.document,
                        created_dt: item.created_dt?.substring(0, 10),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          )}
        </Grid>
        <Grid item xs={12} pt={2}>
          {!!onboardingGuides?.length && (
            <Stack spacing={1} justifyContent='flex-start'>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-start' pl={1}>
                <Document height='22px' width='22px' />
                <Typography variant='h5'> Guides</Typography>
              </Stack>

              <Grid container spacing={1}>
                {onboardingGuides?.map((item) => (
                  <Grid item xs={12} md={6} lg={4} xl={3} key={item.key}>
                    <ArtifactCards
                      data={{
                        ...item,
                        card: CardType.document,
                        created_dt: item.created_dt?.substring(0, 10),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
export default HelpPage;
